import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Leaf, ChevronRight } from 'lucide-react';
import logo from '../Logo.svg';

const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Compliance', href: '#compliance' },
  { label: 'Pricing', href: '#pricing' },
];

export const Navbar = ({ isLanding = false, onTrialClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const close = () => setMobileOpen(false);

  return (
    <>
      {/* ─── Top announcement bar ─── */}
      {isLanding && (
        <div className="bg-gradient-to-r from-emerald-600 via-green-500 to-teal-500 text-white text-center text-xs font-medium py-2 px-4">
          Cannabis compliance made simple —&nbsp;
          <button
            onClick={onTrialClick}
            className="underline underline-offset-2 hover:text-emerald-100 transition-colors font-semibold"
          >
            Start free today
          </button>
          &nbsp;→
        </div>
      )}

      {/* ─── Main header ─── */}
      <header
        className={[
          'sticky top-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-white/95 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.08)] border-b border-emerald-100'
            : 'bg-white border-b border-gray-100',
        ].join(' ')}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* ── Logo ── */}
            <Link to="/" className="flex items-center gap-2.5 shrink-0 group" onClick={close}>
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 shadow-sm group-hover:shadow-md transition-shadow">
                <Leaf className="w-5 h-5 text-white" />
              </div>
              <img
                src={logo}
                alt="Verdionix"
                className="h-7 w-auto"
                style={{ mixBlendMode: 'multiply' }}
              />
            </Link>

            {/* ── Desktop nav links ── */}
            {isLanding && (
              <nav className="hidden md:flex items-center gap-1">
                {NAV_LINKS.map(({ label, href }) => (
                  <a
                    key={label}
                    href={href}
                    className="relative px-4 py-2 text-sm font-medium text-gray-600 hover:text-emerald-700 rounded-full hover:bg-emerald-50 transition-all duration-150 group"
                  >
                    {label}
                    <span className="absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 w-0 bg-emerald-500 rounded-full transition-all duration-200 group-hover:w-4/5" />
                  </a>
                ))}
              </nav>
            )}

            {/* ── Desktop CTAs ── */}
            <div className="hidden md:flex items-center gap-2.5">
              {isLanding ? (
                <>
                  <button
                    onClick={() => navigate('/login')}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-emerald-700 rounded-full hover:bg-emerald-50 transition-all duration-150"
                  >
                    Log in
                  </button>
                  <button
                    onClick={() => navigate('/register')}
                    className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 shadow-sm hover:shadow-[0_4px_14px_rgba(16,185,129,0.45)] transition-all duration-200 active:scale-95"
                  >
                    Get Started
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => navigate('/dashboard')}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-emerald-700 rounded-full hover:bg-emerald-50 transition-all">Dashboard</button>
                  <button onClick={() => navigate('/operators')}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-emerald-700 rounded-full hover:bg-emerald-50 transition-all">Operators</button>
                  <button onClick={() => navigate('/reports')}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-emerald-700 rounded-full hover:bg-emerald-50 transition-all">Reports</button>
                </>
              )}
            </div>

            {/* ── Mobile hamburger ── */}
            <button
              className="md:hidden p-2 rounded-xl text-gray-500 hover:text-emerald-700 hover:bg-emerald-50 transition-all"
              onClick={() => setMobileOpen(v => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* ─── Colorful bottom accent line ─── */}
        <div className="h-[2px] bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400 opacity-60" />
      </header>

      {/* ─── Mobile backdrop ─── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/25 backdrop-blur-sm md:hidden"
          onClick={close}
        />
      )}

      {/* ─── Mobile drawer ─── */}
      <div
        className={[
          'fixed top-0 right-0 bottom-0 z-50 w-72 bg-white flex flex-col md:hidden',
          'shadow-2xl transition-transform duration-300 ease-in-out',
          mobileOpen ? 'translate-x-0' : 'translate-x-full',
        ].join(' ')}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-emerald-600 to-green-500">
          <Link to="/" className="flex items-center gap-2.5" onClick={close}>
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/20">
              <Leaf className="w-4 h-4 text-white" />
            </div>
            <img
              src={logo}
              alt="Verdionix"
              className="h-6 w-auto brightness-0 invert"
            />
          </Link>
          <button
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            onClick={close}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Drawer nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
          {isLanding && NAV_LINKS.map(({ label, href }) => (
            <a
              key={label}
              href={href}
              onClick={close}
              className="flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:text-emerald-700 hover:bg-emerald-50 transition-colors group"
            >
              {label}
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-400 transition-colors" />
            </a>
          ))}
          {!isLanding && ['Dashboard', 'Operators', 'Reports'].map(page => (
            <button
              key={page}
              onClick={() => { navigate(`/${page.toLowerCase()}`); close(); }}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:text-emerald-700 hover:bg-emerald-50 transition-colors group text-left"
            >
              {page}
              <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-emerald-400 transition-colors" />
            </button>
          ))}
        </nav>

        {/* Drawer footer */}
        {isLanding && (
          <div className="px-4 pb-8 pt-3 space-y-2 border-t border-gray-100">
            <button
              className="w-full py-2.5 rounded-xl text-sm font-medium text-emerald-700 border border-emerald-200 hover:bg-emerald-50 transition-colors"
              onClick={() => { navigate('/login'); close(); }}
            >
              Log in
            </button>
            <button
              className="w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-green-500 hover:from-emerald-700 hover:to-green-600 shadow-sm transition-all flex items-center justify-center gap-2"
              onClick={() => { navigate('/register'); close(); }}
            >
              <Leaf className="w-4 h-4" />
              Get Started Free
            </button>
          </div>
        )}
      </div>
    </>
  );
};
